import { type ClassValue, clsx } from 'clsx'
import { formatDateParts, getActiveDateFormat } from './dateFormat'
import { extendTailwindMerge } from 'tailwind-merge'
import { CURRENCY_CATALOG } from '@/config/currencyCatalog'

// The custom font sizes in tailwind.config.js (text-body-sm, text-heading-md...)
// must be registered as font sizes. Otherwise tailwind-merge reads them as text
// colours and drops the real colour beside them: cn('text-white text-body-sm')
// came out as just 'text-body-sm', so the Log defect button lost its white text.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: ['display-lg', 'display-md', 'heading-lg', 'heading-md', 'heading-sm', 'body-lg', 'body-md', 'body-sm', 'body-xs', 'label'] }],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ── Display-currency context ────────────────────────────────────────────────
// Money is rendered through a global context so a company's figures show in its
// native currency (e.g. Codan = AUD → A$) and a user can flip the display
// currency app-wide WITHOUT touching the ~119 format call sites. Identity by
// default (base USD, display USD) so non-AUD demos are completely unaffected.
// Rates + symbols derive from the deployment currency catalog (Client Setup →
// Currencies / VITE_CURRENCIES), so adding a currency there is the ONLY step.
const FX_PER_USD: Record<string, number> = Object.fromEntries(CURRENCY_CATALOG.map((c) => [c.code, c.fxPerUsd]))
const CURRENCY_SYMBOLS: Record<string, string> = { ...Object.fromEntries(CURRENCY_CATALOG.map((c) => [c.code, c.label])), USD: '$' }
let _baseCurrency = 'USD' // currency the raw numbers are denominated in (company native)
let _displayCurrency = 'USD' // currency to render in (native, or a user override)

/** Set the money rendering context: `base` = what the numbers are stored in,
 *  `display` = what to render. Called from appStore on company / override change. */
export function setCurrencyContext(base: string, display: string): void {
  _baseCurrency = base || 'USD'
  _displayCurrency = display || _baseCurrency
}
/** The currency money is currently rendered in. */
export function getDisplayCurrency(): string {
  return _displayCurrency
}
function convertToDisplay(value: number, base: string): number {
  const from = FX_PER_USD[base] ?? 1
  const to = FX_PER_USD[_displayCurrency] ?? 1
  return from === to ? value : (value / from) * to
}

export function formatCurrency(value: number, currency?: string): string {
  const v = convertToDisplay(value, currency ?? _baseCurrency)
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: _displayCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v)
}

export function formatCompactCurrency(value: number, currency?: string): string {
  const v = convertToDisplay(value, currency ?? _baseCurrency)
  const sym = CURRENCY_SYMBOLS[_displayCurrency] ?? `${_displayCurrency} `
  if (v >= 1_000_000) {
    return `${sym}${(v / 1_000_000).toFixed(1)}M`
  }
  if (v >= 1_000) {
    return `${sym}${(v / 1_000).toFixed(0)}K`
  }
  return formatCurrency(value, currency)
}

export function formatPercent(value: number): string {
  return `${Math.round(value * 100) / 100}%`
}

/** A date-only value with no time part, e.g. an effective date: 'YYYY-MM-DD'. */
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

export function formatDate(dateStr: string): string {
  // The Date constructor reads a bare 'YYYY-MM-DD' as UTC midnight, which then
  // renders as the PREVIOUS day everywhere west of Greenwich - an effective
  // date of '2026-01-01' showed as "Dec 31, 2025" in Phoenix. Build date-only
  // values from their parts so they land on local midnight. Real timestamps
  // (with a time part or a zone) are left to the constructor: their local-time
  // rendering is correct and must not change.
  const m = DATE_ONLY_RE.exec(dateStr)
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(dateStr)
  // The user's own date format (My Profile), when they have chosen one.
  const preferred = getActiveDateFormat()
  if (preferred && !Number.isNaN(d.getTime())) {
    return formatDateParts(d.getFullYear(), d.getMonth() + 1, d.getDate(), preferred)
  }
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function getHealthColor(status: string): string {
  switch (status) {
    case 'Green': return 'text-status-green'
    case 'Yellow': return 'text-status-yellow'
    case 'Red': return 'text-status-red'
    default: return 'text-text-secondary'
  }
}

export function getHealthBg(status: string): string {
  switch (status) {
    case 'Green': return 'bg-status-green-bg'
    case 'Yellow': return 'bg-status-yellow-bg'
    case 'Red': return 'bg-status-red-bg'
    default: return 'bg-surface-muted'
  }
}

export function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'Green':
    case 'Completed':
    case 'In Progress':
      return 'badge-green'
    case 'Yellow':
    case 'At Risk':
    case 'On Hold':
      return 'badge-yellow'
    case 'Red':
    case 'Cancelled':
      return 'badge-red'
    case 'Planning':
      return 'badge-blue'
    default:
      return 'badge-blue'
  }
}

export function generateId(): string {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}
